import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ListX, Loader2, Plus, RefreshCw, TriangleAlert, Wand2, X } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ConnectorType, FieldMapping, MappingConfig } from '../types';
import { ARDEALY_TARGET_FIELDS, SOURCE_FIELD_SCHEMAS, AUTO_MAPPING_PRESETS, FieldDefinition } from '../constants/field-mappings';

interface DataMappingPanelProps {
  connectorType: ConnectorType;
  config: MappingConfig;
  onSave: (config: MappingConfig) => void;
  connectorConfig?: Record<string, any>;
  onFetchSample?: (connectorType: ConnectorType, connectorConfig?: Record<string, any>) => Promise<{ success: boolean; product?: Record<string, any>; error?: string }>;
  onAiMap?: (
    params: { connectorType: string; connectorConfig?: Record<string, any> },
    onEvent: (event: { type: string; data: any }) => void,
    onDone: () => void,
    onError: (err: string) => void,
  ) => AbortController;
}

let mappingIdCounter = 100;

const truncateValue = (val: any, max = 40): string => {
  if (val == null) return '';
  const str = String(val);
  return str.length > max ? str.slice(0, max) + '...' : str;
};

const isImageUrl = (val: any): boolean => {
  if (typeof val !== 'string') return false;
  return /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(val) ||
    val.includes('cdn.shopify.com') ||
    val.startsWith('https://') && val.includes('/images/');
};

/** Tiny metadata chip on a field row (custom / array / ×N) — repeated everywhere. */
const FieldChip: React.FC<{ children: React.ReactNode; mono?: boolean; testId?: string }> = ({ children, mono, testId }) => (
  <span
    data-testid={testId}
    className={cn(
      'me-1 shrink-0 rounded-md border bg-muted px-1 py-px text-[9px] font-medium leading-tight text-muted-foreground',
      mono && 'font-mono',
    )}
  >
    {children}
  </span>
);

/** Tiny remove/unmap button that fits inside a 30px field row. */
const RowIconButton: React.FC<{ onClick: (e: React.MouseEvent) => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="ms-1 flex size-4 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-destructive"
  >
    {children}
  </button>
);

/* ─── Draggable source field ─── */
const DraggableSourceField: React.FC<{
  field: FieldDefinition;
  mapped: boolean;
  isSelected: boolean;
  onClick: () => void;
  isCustom?: boolean;
  onRemove?: () => void;
  onUnmap?: () => void;
}> = ({ field, mapped, isSelected, onClick, isCustom, onRemove, onUnmap }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `source-${field.field}`,
    data: { sourceField: field.field },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`source-field-${field.field}`}
      onClick={onClick}
      className={cn(
        'flex h-[30px] cursor-grab items-center rounded-lg border px-2 transition-colors hover:border-foreground/60',
        isSelected
          ? 'border-foreground bg-muted'
          : mapped
            ? 'border-foreground/40 bg-muted/60'
            : 'border-border bg-background',
        isDragging && 'opacity-40',
      )}
    >
      <span className={cn('me-1.5 size-1.5 shrink-0 rounded-full', mapped ? 'bg-foreground' : 'bg-border')} />
      <span
        className={cn(
          'min-w-0 flex-1 truncate font-mono text-xs',
          mapped ? 'font-medium text-foreground' : 'text-muted-foreground',
        )}
      >
        {field.labelKey}
      </span>
      {isCustom && <FieldChip>custom</FieldChip>}
      {mapped && onUnmap ? (
        <RowIconButton onClick={(e) => { e.stopPropagation(); onUnmap(); }}>
          <X className="size-3" />
        </RowIconButton>
      ) : isCustom && onRemove ? (
        <RowIconButton onClick={(e) => { e.stopPropagation(); onRemove(); }}>
          <X className="size-3" />
        </RowIconButton>
      ) : null}
    </div>
  );
};

/* ─── Droppable target field ─── */
const DroppableTargetField: React.FC<{
  field: FieldDefinition;
  mapped: boolean;
  isDropTarget: boolean;
  onClick: () => void;
  hasSelectedSource: boolean;
  isCustom?: boolean;
  onRemove?: () => void;
  mappingCount?: number;
}> = ({ field, mapped, isDropTarget, onClick, hasSelectedSource, isCustom, onRemove, mappingCount = 0 }) => {
  const { t } = useTranslation();
  const { isOver, setNodeRef } = useDroppable({
    id: `target-${field.field}`,
    data: { targetField: field.field },
  });

  const highlight = isOver || isDropTarget;

  return (
    <div
      ref={setNodeRef}
      data-testid={`target-field-${field.field}`}
      onClick={onClick}
      className={cn(
        'flex h-[30px] items-center rounded-lg border px-2 transition-colors',
        highlight
          ? 'border-foreground bg-muted'
          : mapped
            ? 'border-foreground/40 bg-muted/60'
            : 'border-border bg-background',
        hasSelectedSource ? 'cursor-pointer hover:border-foreground/60' : 'cursor-default',
        !hasSelectedSource && !mapped && 'opacity-70',
      )}
    >
      <span className={cn('me-1.5 size-1.5 shrink-0 rounded-full', mapped ? 'bg-foreground' : 'bg-border')} />
      {mappingCount > 1 && (
        <FieldChip mono testId={`target-count-${field.field}`}>×{mappingCount}</FieldChip>
      )}
      <span
        className={cn(
          'min-w-0 flex-1 truncate font-mono text-xs',
          mapped ? 'font-medium text-foreground' : 'text-muted-foreground',
        )}
      >
        {t(field.labelKey)}
      </span>
      {field.isArray && <FieldChip mono>[ ]</FieldChip>}
      {field.required && (
        <span className="text-[9px] font-medium text-destructive">*</span>
      )}
      {isCustom && (
        <span className="flex items-center">
          <FieldChip>custom</FieldChip>
          {onRemove && (
            <RowIconButton onClick={(e) => { e.stopPropagation(); onRemove(); }}>
              <X className="size-3" />
            </RowIconButton>
          )}
        </span>
      )}
    </div>
  );
};

/* ─── Main Panel ─── */
export const DataMappingPanel: React.FC<DataMappingPanelProps> = ({
  connectorType,
  config,
  onSave,
  connectorConfig,
  onFetchSample,
  onAiMap,
}) => {
  const { t } = useTranslation();
  const [mappings, setMappings] = useState<FieldMapping[]>(config.mappings);
  const [autoMapped, setAutoMapped] = useState(config.autoMapped);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [draggingSource, setDraggingSource] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sample product state — drives both source fields and preview values
  const [sampleProduct, setSampleProduct] = useState<Record<string, any> | null>(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);
  const hasAutoMappedOnOpenRef = useRef(false);

  // AI mapping state
  const [aiMapping, setAiMapping] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiStatus, setAiStatus] = useState<string | null>(null);
  const aiAbortRef = useRef<AbortController | null>(null);

  // Custom fields added manually by the user
  const [customSourceFields, setCustomSourceFields] = useState<FieldDefinition[]>([]);
  const [customTargetFields, setCustomTargetFields] = useState<FieldDefinition[]>([]);
  // Fields explicitly added from the dropdown (tracked by insertion order)
  const [pinnedSourceFields, setPinnedSourceFields] = useState<string[]>([]);
  const [addingCustomTarget, setAddingCustomTarget] = useState(false);
  const [customTargetInput, setCustomTargetInput] = useState('');

  // Add-source dropdown state (plain input + filtered list)
  const [sourceQuery, setSourceQuery] = useState('');
  const [sourceListOpen, setSourceListOpen] = useState(false);

  // Source fields: derived from sample product when available, static fallback otherwise
  const fallbackSourceFields = SOURCE_FIELD_SCHEMAS[connectorType] || [];
  const fetchedSourceFields: FieldDefinition[] = sampleProduct
    ? Object.keys(sampleProduct)
        .filter(key => key !== 'info')
        .map(key => ({ field: key, labelKey: key }))
    : [];
  const liveSourceFields = sampleProduct ? fetchedSourceFields : fallbackSourceFields;

  // Recover orphaned fields: source/target fields referenced in saved mappings
  // that aren't in the live or manually-added custom lists (e.g. after dialog reopen)
  const knownSourceSet = new Set([...liveSourceFields.map(f => f.field), ...customSourceFields.map(f => f.field)]);
  const orphanedSourceFields: FieldDefinition[] = mappings
    .filter(m => !knownSourceSet.has(m.sourceField))
    .map(m => ({ field: m.sourceField, labelKey: m.sourceField }));

  const baseTargetFields = ARDEALY_TARGET_FIELDS;
  const knownTargetSet = new Set([...baseTargetFields.map(f => f.field), ...customTargetFields.map(f => f.field)]);
  const orphanedTargetFields: FieldDefinition[] = mappings
    .filter(m => !knownTargetSet.has(m.targetField))
    .map(m => ({ field: m.targetField, labelKey: m.targetField }));

  const sourceFields = [...liveSourceFields, ...customSourceFields, ...orphanedSourceFields];
  const targetFields = [...baseTargetFields, ...customTargetFields, ...orphanedTargetFields];

  const filteredTargetFields = targetFields;

  // Track which fields came from the live fetch vs user-added/orphaned custom
  const liveFieldSet = new Set(liveSourceFields.map(f => f.field));
  const predefinedTargetFieldSet = new Set(baseTargetFields.map(f => f.field));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => {
    setMappings(config.mappings);
    setAutoMapped(config.autoMapped);
  }, [config]);

  // Auto-fetch sample product on mount
  useEffect(() => {
    if (onFetchSample && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchSample();
    }
    return () => {
      hasFetchedRef.current = false;
      hasAutoMappedOnOpenRef.current = false;
      if (aiAbortRef.current) {
        aiAbortRef.current.abort();
        aiAbortRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSample = useCallback(async () => {
    if (!onFetchSample) return;
    setFetchLoading(true);
    setFetchError(null);
    try {
      const result = await onFetchSample(connectorType, connectorConfig);
      if (result.success && result.product) {
        setSampleProduct(result.product);
      } else {
        setFetchError(result.error || 'Failed to fetch fields from store');
      }
    } catch {
      setFetchError('Failed to fetch fields from store');
    } finally {
      setFetchLoading(false);
    }
  }, [onFetchSample, connectorType, connectorConfig]);

  const handlePresetAutoMap = useCallback(() => {
    const preset = AUTO_MAPPING_PRESETS[connectorType];
    if (!preset) return;
    // Only include preset mappings whose source field actually exists
    const availableSourceSet = new Set(sourceFields.map(f => f.field));
    const filtered = preset
      .filter(m => availableSourceSet.has(m.sourceField))
      .map(m => ({ ...m, id: `auto-${++mappingIdCounter}` }));
    // Auto-map all image source fields to array target fields (e.g. images[N].src → gallery_images)
    const arrayTargets = targetFields.filter(f => f.isArray);
    for (const target of arrayTargets) {
      const imagePattern = /^images\[\d+\]\.src$/;
      const imageSources = sourceFields
        .filter(f => imagePattern.test(f.field))
        .sort((a, b) => a.field.localeCompare(b.field, undefined, { numeric: true }));
      for (const src of imageSources) {
        filtered.push({ id: `auto-${++mappingIdCounter}`, sourceField: src.field, targetField: target.field });
      }
    }
    setMappings(filtered);
    setAutoMapped(true);
  }, [connectorType, sourceFields, targetFields]);

  const handleAutoMap = useCallback(() => {
    // Use AI mapping when available and sample product exists
    if (onAiMap && sampleProduct) {
      setAiMapping(true);
      setAiError(null);

      aiAbortRef.current = onAiMap(
        { connectorType, connectorConfig },
        (event) => {
          if (event.type === 'thinking') {
            setAiStatus(event.data?.message || 'Thinking...');
          } else if (event.type === 'tool_call') {
            setAiStatus('Mapping fields...');
          } else if (event.type === 'tool_result' && event.data?.tool === 'set_mappings') {
            const aiMappings: Array<{ sourceField: string; targetField: string }> =
              event.data?.result?.mappings || [];
            const withIds = aiMappings.map(m => ({
              ...m,
              id: `ai-${++mappingIdCounter}`,
            }));
            setMappings(withIds);
            setAutoMapped(true);
            setAiStatus(null);
          } else if (event.type === 'error') {
            setAiMapping(false);
            setAiStatus(null);
            setAiError(event.data?.message || 'AI mapping failed');
            setTimeout(() => setAiError(null), 4000);
            handlePresetAutoMap();
          }
        },
        () => {
          setAiMapping(false);
          setAiStatus(null);
        },
        (err) => {
          setAiMapping(false);
          setAiStatus(null);
          setAiError(err);
          setTimeout(() => setAiError(null), 4000);
          handlePresetAutoMap();
        },
      );
      return;
    }

    // Fallback to preset mapping
    handlePresetAutoMap();
  }, [onAiMap, sampleProduct, connectorType, connectorConfig, handlePresetAutoMap]);

  // Auto-map when config is new (no existing mappings) and sample data has loaded
  useEffect(() => {
    if (
      sampleProduct &&
      config.mappings.length === 0 &&
      !hasAutoMappedOnOpenRef.current
    ) {
      hasAutoMappedOnOpenRef.current = true;
      // Use the static preset on first open — it's free and instant. The
      // AI Auto-Mapper only runs when the user explicitly clicks the button,
      // so we don't burn a request on a first-mount race that often fails.
      handlePresetAutoMap();
    }
  }, [sampleProduct, config.mappings.length, handlePresetAutoMap]);

  const handleClearAll = useCallback(() => {
    setMappings([]);
    setAutoMapped(false);
    setSelectedSource(null);
    setPinnedSourceFields([]);
  }, []);

  const removeMapping = useCallback((sourceField: string) => {
    setMappings(prev => prev.filter(m => m.sourceField !== sourceField));
    setAutoMapped(false);
  }, []);

  const handleSourceClick = useCallback((field: string) => {
    setSelectedSource(prev => prev === field ? null : field);
  }, []);

  const handleTargetClick = useCallback((field: string) => {
    if (!selectedSource) return;

    const filtered = mappings.filter(m => m.sourceField !== selectedSource);
    filtered.push({
      id: `map-${++mappingIdCounter}`,
      sourceField: selectedSource,
      targetField: field,
    });
    setMappings(filtered);
    setAutoMapped(false);
    setPinnedSourceFields(prev => prev.filter(f => f !== selectedSource));
    setSelectedSource(null);
  }, [selectedSource, mappings]);

  const createMapping = useCallback((sourceField: string, targetField: string) => {
    const filtered = mappings.filter(m => m.sourceField !== sourceField);
    filtered.push({
      id: `map-${++mappingIdCounter}`,
      sourceField,
      targetField,
    });
    setMappings(filtered);
    setAutoMapped(false);
    setPinnedSourceFields(prev => prev.filter(f => f !== sourceField));
    setSelectedSource(null);
  }, [mappings]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const sourceField = event.active.data.current?.sourceField as string;
    if (sourceField) {
      setDraggingSource(sourceField);
      setSelectedSource(null);
    }
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setDraggingSource(null);

    if (!over) return;

    const sourceField = active.data.current?.sourceField as string;
    const targetField = over.data.current?.targetField as string;

    if (sourceField && targetField) {
      createMapping(sourceField, targetField);
    }
  }, [createMapping]);

  const handleSave = () => {
    onSave({ mappings, autoMapped });
  };

  const handleRemoveCustomSource = useCallback((field: string) => {
    setCustomSourceFields(prev => prev.filter(f => f.field !== field));
    setMappings(prev => prev.filter(m => m.sourceField !== field));
  }, []);

  const handleAddCustomTarget = useCallback(() => {
    const trimmed = customTargetInput.trim();
    if (!trimmed) return;
    const allFields = new Set(targetFields.map(f => f.field));
    if (allFields.has(trimmed)) return;
    setCustomTargetFields(prev => [...prev, { field: trimmed, labelKey: trimmed }]);
    setCustomTargetInput('');
    setAddingCustomTarget(false);
  }, [customTargetInput, targetFields]);

  const handleRemoveCustomTarget = useCallback((field: string) => {
    setCustomTargetFields(prev => prev.filter(f => f.field !== field));
    setMappings(prev => prev.filter(m => m.targetField !== field));
  }, []);

  // Commit a value from the add-source dropdown: create it if custom, pin it,
  // and select it for connecting — same contract the old Autocomplete had.
  const commitSourceField = useCallback((raw: string) => {
    const value = raw.trim();
    if (!value) return;
    if (!sourceFields.some(f => f.field === value)) {
      setCustomSourceFields(prev => [...prev, { field: value, labelKey: value }]);
    }
    setPinnedSourceFields(prev => prev.includes(value) ? prev : [...prev, value]);
    setSelectedSource(value);
    setSourceQuery('');
    setSourceListOpen(false);
  }, [sourceFields]);

  const isMapped = (field: string, side: 'source' | 'target') =>
    mappings.some(m => side === 'source' ? m.sourceField === field : m.targetField === field);

  // Source column: mapped fields first (in their natural order), then pinned/selected at the bottom (in insertion order)
  const pinnedSet = new Set(pinnedSourceFields);
  const mappedSourceFieldsList = sourceFields.filter(f =>
    isMapped(f.field, 'source') && !pinnedSet.has(f.field)
  );
  const pinnedSourceFieldsList = pinnedSourceFields
    .map(field => sourceFields.find(f => f.field === field))
    .filter((f): f is FieldDefinition => !!f);
  const selectedField = selectedSource && !isMapped(selectedSource, 'source') && !pinnedSet.has(selectedSource)
    ? sourceFields.find(f => f.field === selectedSource)
    : null;
  const filteredSourceFields = [
    ...mappedSourceFieldsList,
    ...pinnedSourceFieldsList,
    ...(selectedField ? [selectedField] : []),
  ];
  const visibleSourceSet = new Set(filteredSourceFields.map(f => f.field));
  const unmappedSourceFields = sourceFields.filter(f => !visibleSourceSet.has(f.field));

  const sourceOptions = unmappedSourceFields
    .map(f => f.field)
    .filter(f => !sourceQuery.trim() || f.toLowerCase().includes(sourceQuery.trim().toLowerCase()));

  // Validation summary computed values
  const mappedSourceCount = sourceFields.filter(f => isMapped(f.field, 'source')).length;
  const totalSourceCount = sourceFields.length;
  const missingRequired = targetFields.filter(f => f.required && !isMapped(f.field, 'target'));

  const draggingField = draggingSource ? sourceFields.find(f => f.field === draggingSource) : null;

  // Connector line positions measured from DOM
  const [connectorLines, setConnectorLines] = useState<Array<{ id: string; x1: number; y1: number; x2: number; y2: number }>>([]);

  useEffect(() => {
    if (mappings.length === 0) {
      // Bail to the same reference when already empty — the deps below get a
      // fresh identity every render, so an unconditional set would loop.
      setConnectorLines(prev => (prev.length === 0 ? prev : []));
      return;
    }
    const frame = requestAnimationFrame(() => {
      const container = containerRef.current;
      if (!container) return;
      const containerRect = container.getBoundingClientRect();
      const lines: Array<{ id: string; x1: number; y1: number; x2: number; y2: number }> = [];

      for (const mapping of mappings) {
        const escapedSource = CSS.escape(`source-field-${mapping.sourceField}`);
        const escapedTarget = CSS.escape(`target-field-${mapping.targetField}`);
        const sourceEl = container.querySelector(`[data-testid="${escapedSource}"]`);
        const targetEl = container.querySelector(`[data-testid="${escapedTarget}"]`);
        if (!sourceEl || !targetEl) continue;

        const srcRect = sourceEl.getBoundingClientRect();
        const tgtRect = targetEl.getBoundingClientRect();

        lines.push({
          id: mapping.id,
          x1: srcRect.right - containerRect.left,
          y1: srcRect.top + srcRect.height / 2 - containerRect.top,
          x2: tgtRect.left - containerRect.left,
          y2: tgtRect.top + tgtRect.height / 2 - containerRect.top,
        });
      }
      // Same-value bail-out, for the same reason as the empty branch above.
      setConnectorLines(prev =>
        prev.length === lines.length &&
        prev.every((p, i) =>
          p.id === lines[i].id && p.x1 === lines[i].x1 && p.y1 === lines[i].y1 &&
          p.x2 === lines[i].x2 && p.y2 === lines[i].y2)
          ? prev
          : lines
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [mappings, filteredSourceFields, filteredTargetFields, selectedSource, sampleProduct]);

  const getSourcesForTarget = (targetField: string) =>
    mappings.filter(m => m.targetField === targetField).map(m => m.sourceField);

  const columnHeadingClass = 'mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground';

  return (
    // Local provider: the panel is also rendered outside the app shell (tests, dialogs).
    <TooltipProvider>
    <div className="relative flex h-full flex-col">
      {/* Full overlay loader while fetching fields or running AI mapping */}
      {(fetchLoading || aiMapping) && (
        <div
          data-testid="source-fields-loading"
          className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-background/90"
        >
          <Loader2 className="size-7 animate-spin text-foreground" />
          <p className="text-sm text-muted-foreground">
            {fetchLoading
              ? t('PAGES.API_INTEGRATION.FETCHING_FIELDS')
              : (aiStatus || 'AI mapping...')}
          </p>
        </div>
      )}
      <div>

        {/* Error fetching fields — show inline warning with retry */}
        {fetchError && !fetchLoading && (
          <div
            data-testid="source-fields-error"
            className="mb-4 flex items-center justify-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3"
          >
            <TriangleAlert className="size-4 shrink-0 text-warning" />
            <p className="flex-1 text-sm text-foreground">
              {fetchError} — {t('PAGES.API_INTEGRATION.USING_FALLBACK_FIELDS')}
            </p>
            <Button variant="ghost" size="sm" onClick={fetchSample}>
              <RefreshCw className="size-3.5" />
              {t('PAGES.API_INTEGRATION.RETRY')}
            </Button>
          </div>
        )}

        {/* Validation Summary Bar */}
        <div data-testid="validation-summary" className="mb-3 flex items-center justify-between">
          <div data-testid="mapping-count" className="flex items-center gap-1 bg-muted px-3 py-1">
            <span className="text-sm font-medium text-foreground">
              {mappedSourceCount} / {totalSourceCount}
            </span>
            <span className="text-sm text-muted-foreground">
              {t('PAGES.API_INTEGRATION.FIELDS_MAPPED_OF')}
            </span>
          </div>
          {missingRequired.length > 0 && (
            <div
              data-testid="missing-required-summary"
              className="flex items-center gap-1.5 rounded-lg border border-warning/40 bg-warning/10 px-3 py-1"
            >
              <TriangleAlert className="size-3.5 shrink-0 text-warning" />
              <span className="text-sm font-medium text-foreground">
                {missingRequired.length} {t('PAGES.API_INTEGRATION.REQUIRED_MISSING')}:
              </span>
              <span className="font-mono text-sm text-muted-foreground">
                {missingRequired.map(f => f.field).join(', ')}
              </span>
            </div>
          )}
        </div>

        {/* Global hint — lives outside the column grid so column row heights
            stay equal across Source / Target / Preview. */}
        <p className="mb-3 text-xs leading-normal text-muted-foreground">
          {t('PAGES.API_INTEGRATION.TARGET_FIELDS_HINT', 'Mapped fields will be added as custom product fields.')}
        </p>

        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div
            ref={containerRef}
            className={cn(
              'relative grid min-h-[400px] transition-opacity',
              aiMapping && 'pointer-events-none opacity-50',
            )}
            style={{
              gridTemplateColumns: sampleProduct && !sampleProduct.info
                ? '1fr 80px 1fr 0.7fr'
                : '1fr 80px 1fr',
            }}
          >
            {/* SVG Connector Overlay — geometry measured from the DOM rows */}
            <svg className="pointer-events-none absolute top-0 left-0 z-[1] h-full w-full overflow-visible">
              {connectorLines.map(line => {
                const dx = Math.abs(line.x2 - line.x1);
                const cpOffset = Math.max(dx * 0.45, 35);
                return (
                  <g key={line.id} opacity={0.45}>
                    <path
                      d={`M ${line.x1} ${line.y1} C ${line.x1 + cpOffset} ${line.y1}, ${line.x2 - cpOffset} ${line.y2}, ${line.x2} ${line.y2}`}
                      fill="none"
                      stroke="var(--foreground)"
                      strokeWidth="1.5"
                    />
                    <circle cx={line.x1} cy={line.y1} r={3} fill="var(--foreground)" />
                    <circle cx={line.x2} cy={line.y2} r={3} fill="var(--foreground)" />
                  </g>
                );
              })}
            </svg>

            {/* Source Fields Column */}
            <div>
              <p className={columnHeadingClass}>
                {t('PAGES.API_INTEGRATION.SOURCE_FIELDS')}
              </p>
              {/* Mapped source fields */}
              <div className="flex flex-col gap-[3px]">
                {filteredSourceFields.map(field => (
                  <DraggableSourceField
                    key={field.field}
                    field={field}
                    mapped={isMapped(field.field, 'source')}
                    isSelected={selectedSource === field.field}
                    onClick={() => handleSourceClick(field.field)}
                    isCustom={!liveFieldSet.has(field.field)}
                    onRemove={!liveFieldSet.has(field.field) ? () => handleRemoveCustomSource(field.field) : undefined}
                    onUnmap={isMapped(field.field, 'source') ? () => removeMapping(field.field) : undefined}
                  />
                ))}
              </div>
              {/* Dropdown to add unmapped source fields (free text allowed) */}
              <div className="relative mt-1" data-testid="source-field-dropdown">
                <Input
                  data-testid="source-field-autocomplete"
                  className="h-7 font-mono text-xs"
                  placeholder={t('PAGES.API_INTEGRATION.ADD_SOURCE_FIELD', 'Add source field...')}
                  value={sourceQuery}
                  onChange={(e) => { setSourceQuery(e.target.value); setSourceListOpen(true); }}
                  onFocus={() => setSourceListOpen(true)}
                  onBlur={() => { window.setTimeout(() => setSourceListOpen(false), 150); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitSourceField(sourceQuery); }
                    if (e.key === 'Escape') setSourceListOpen(false);
                  }}
                />
                {sourceListOpen && sourceOptions.length > 0 && (
                  <div className="absolute inset-x-0 top-full z-30 mt-1 max-h-48 overflow-y-auto rounded-lg border bg-background shadow-md">
                    {sourceOptions.map(option => (
                      <button
                        key={option}
                        type="button"
                        // preventDefault so the input's blur doesn't close the list before the click lands
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => commitSourceField(option)}
                        className="block w-full truncate px-2 py-1 text-start font-mono text-xs text-foreground hover:bg-accent"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Connector gap — SVG lines render here */}
            <div />

            {/* Target Fields Column */}
            <div>
              <p className={columnHeadingClass}>
                {t('PAGES.API_INTEGRATION.TARGET_FIELDS_LABEL')}
              </p>
              <div className="flex flex-col gap-[3px]">
                {filteredTargetFields.map(field => (
                  <DroppableTargetField
                    key={field.field}
                    field={field}
                    mapped={isMapped(field.field, 'target')}
                    isDropTarget={false}
                    onClick={() => handleTargetClick(field.field)}
                    hasSelectedSource={!!selectedSource || !!draggingSource}
                    isCustom={!predefinedTargetFieldSet.has(field.field)}
                    onRemove={!predefinedTargetFieldSet.has(field.field) ? () => handleRemoveCustomTarget(field.field) : undefined}
                    mappingCount={getSourcesForTarget(field.field).length}
                  />
                ))}
              </div>
              {/* Add custom target field */}
              {addingCustomTarget ? (
                <div className="mt-1 flex items-center gap-1">
                  <Input
                    className="h-7 flex-1 font-mono text-xs"
                    placeholder="field_name"
                    value={customTargetInput}
                    onChange={(e) => setCustomTargetInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddCustomTarget(); if (e.key === 'Escape') setAddingCustomTarget(false); }}
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={handleAddCustomTarget}
                    aria-label={t('PAGES.API_INTEGRATION.ADD_CUSTOM_TARGET', 'Add custom field') as string}
                  >
                    <Plus className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => setAddingCustomTarget(false)}
                    aria-label={t('COMMON.CANCEL') as string}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1 text-muted-foreground"
                  onClick={() => setAddingCustomTarget(true)}
                  data-testid="add-custom-target-btn"
                >
                  <Plus className="size-3.5" />
                  {t('PAGES.API_INTEGRATION.ADD_CUSTOM_FIELD')}
                </Button>
              )}
            </div>

            {/* Preview Column — shows sample values for mapped target fields */}
            {sampleProduct && !sampleProduct.info && (
              <div className="ps-2">
                <p className={columnHeadingClass}>
                  {t('PAGES.API_INTEGRATION.PREVIEW_TITLE', 'Preview')}
                </p>
                <div className="flex flex-col gap-[3px]">
                  {filteredTargetFields.map(field => {
                    const sources = getSourcesForTarget(field.field);
                    const hasMapping = sources.length > 0;

                    // Collect sample values from all mapped source fields
                    const values = hasMapping
                      ? sources
                          .map(src => sampleProduct[src])
                          .filter(v => v !== undefined && v !== null)
                      : [];

                    const hasValue = values.length > 0;
                    // Check if any value is an image URL
                    const imageVal = values.find(v => isImageUrl(v));

                    return (
                      <div
                        key={`preview-${field.field}`}
                        data-testid={`preview-value-${field.field}`}
                        className={cn(
                          'flex h-[30px] items-center overflow-hidden rounded-lg border px-1.5',
                          hasValue ? 'border-border bg-muted/50' : 'border-transparent',
                        )}
                      >
                        {imageVal && (
                          <img
                            src={String(imageVal)}
                            alt=""
                            className="me-1 size-5 shrink-0 rounded-sm border object-cover"
                          />
                        )}
                        {hasValue ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="truncate font-mono text-xs text-muted-foreground">
                                {truncateValue(values.map(v => String(v)).join(', '), 25)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              {values.map(v => String(v)).join(', ')}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className="font-mono text-xs text-muted-foreground/70">
                            {hasMapping ? '—' : ''}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Drag Overlay */}
          <DragOverlay dropAnimation={null}>
            {draggingField && (
              <div className="flex h-[30px] w-[180px] cursor-grabbing items-center rounded-lg border border-foreground bg-muted px-2 shadow-md">
                <span className="me-1.5 size-1.5 shrink-0 rounded-full bg-foreground" />
                <span className="truncate font-mono text-xs font-medium text-foreground">
                  {draggingField.labelKey}
                </span>
              </div>
            )}
          </DragOverlay>
        </DndContext>

      </div>

      <div className="mt-auto flex items-center justify-between pt-4">
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  onClick={handleAutoMap}
                  disabled={aiMapping}
                  variant="outline"
                  data-testid="auto-map-button"
                >
                  <Wand2 className="size-4" />
                  {t('PAGES.API_INTEGRATION.AUTO_MAP')}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{t('PAGES.API_INTEGRATION.AUTO_MAP_TOOLTIP')}</TooltipContent>
          </Tooltip>
          {aiError && (
            <p data-testid="ai-map-error" className="text-xs text-destructive">
              {aiError}
            </p>
          )}
          <Button onClick={handleClearAll} variant="outline">
            <ListX className="size-4" />
            {t('PAGES.API_INTEGRATION.CLEAR_ALL')}
          </Button>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleSave} variant="outline">
            {t('PAGES.API_INTEGRATION.SAVE')}
          </Button>
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
};
